import { UUID } from "@bitory-gmbh/nidavellir";
import { documentDbPut } from "@/shared/lib/storage/indexdb/document-db";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import t from "@/lang/lang";
import {
  Query,
  useDocumentDBGetAll,
} from "@/shared/lib/storage/indexdb/document-db-hooks";
import {
  useHtzOrderListQuery,
  useLazyHtzOrderListQuery,
} from "@/services/backend/htz/orders/api/order-api";
import { Order } from "@/services/backend/htz/orders/types/order";
import { useParams } from "react-router";
import { useStaleContext } from "./stale-context";
import { OfflineState, useOfflineContext } from "./offline-context";

export function useOrdersOffline(workOrderId: UUID, processId: string) {
  const { setSynced } = useOfflineContext();
  const { addStale } = useStaleContext();
  const [load] = useLazyHtzOrderListQuery();

  const prepMsg = t("Speichere Aufträge ...");
  const doneMsg = t("Aufträge");

  const prepare = async () => {
    const result = await load({
      process: { active: true, values: [processId] },
    });
    if (result.error) throw result.error;

    addStale("orders");

    setSynced((prev) => [...prev, prepMsg]);

    await Promise.all(
      (result.data!.items ?? []).map(async (item) => {
        await documentDbPut<Order>(
          GSMDocumentKind.WorkOrder,
          workOrderId,
          "orders",
          item,
        );
      }),
    );

    setSynced((prev) => prev.map((p) => (p === prepMsg ? doneMsg : p)));
  };

  return { prepare };
}

export function useGetOrders(processId: string): Query<Order[]> {
  const { offlineState } = useOfflineContext();
  const { workOrderId } = useParams();

  const {
    data: serverData,
    isLoading: serverIsLoading,
    error: serverError,
  } = useHtzOrderListQuery({
    process: { active: true, values: [processId] },
  });

  const {
    data: indexedDBData,
    isLoading: indexedDBIsLoading,
    error: indexedDBError,
  } = useDocumentDBGetAll<Order>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "orders",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: serverData?.items,
        isLoading: serverIsLoading,
        error: serverError,
      };
    case OfflineState.OFFLINE:
      return {
        data: indexedDBData,
        isLoading: indexedDBIsLoading,
        error: indexedDBError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: undefined,
      };
  }
}
